import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Scroll-driven word reveal, shared by every `.bskr-display-reveal-text`
// section: the bskr/display-reveal-text block AND the
// bskr/layout-hero-breadcrumb-reveal layout. It lives as a global partial (not
// a per-block script) because the hero layout nests it and per-block scripts
// aren't reliably enqueued inside nested layout blocks.
//
// Frontend: the headline's words are wrapped in <span class="reveal-w"> and
// animated from muted to full opacity, scrubbed to scroll. Only opacity
// animates (no blur, no transform) so it stays cheap.
//
// Editor: do nothing — the heading is an editable core/heading inner block, so
// splitting its text into spans would break editing. wp.data only exists inside
// Gutenberg.

function start() {
  if (typeof window !== "undefined" && window.wp && window.wp.data) {
    return;
  }
  initReveal();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}

function initReveal() {
  const sections = document.querySelectorAll(".bskr-display-reveal-text");
  if (!sections.length) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  sections.forEach((section) => {
    const headline =
      section.querySelector(".bskr-display-reveal-text__headline") ||
      section.querySelector("h1, h2");
    if (!headline || headline.dataset.revealReady === "1") return;
    headline.dataset.revealReady = "1";

    // Reduced motion: leave the heading fully legible, no split, no animation.
    if (reduceMotion) return;

    const words = [];
    wrapWords(headline, words);
    if (!words.length) return;

    // Only now flag the section so the muted initial state kicks in — keeps the
    // heading readable if the script never reaches this point.
    section.classList.add("is-reveal-ready");

    gsap.to(words, {
      opacity: 1,
      ease: "none",
      stagger: 0.5,
      scrollTrigger: {
        trigger: section,
        start: "top 25%",
        end: "bottom 75%",
        scrub: true,
      },
    });
  });
}

// Recursively wrap each word in the heading with <span class="reveal-w">,
// preserving whitespace and any inline markup (e.g. <strong> stays bold).
function wrapWords(node, collector) {
  // Snapshot childNodes first: we replace text nodes in place while iterating.
  const children = Array.from(node.childNodes);

  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent;
      if (!text) continue;

      const frag = document.createDocumentFragment();
      // Split keeping the whitespace tokens so spacing is untouched.
      const tokens = text.split(/(\s+)/);

      for (const token of tokens) {
        if (token.length === 0) continue;
        if (/^\s+$/.test(token)) {
          frag.appendChild(document.createTextNode(token));
        } else {
          const span = document.createElement("span");
          span.className = "reveal-w";
          span.textContent = token;
          frag.appendChild(span);
          collector.push(span);
        }
      }

      node.replaceChild(frag, child);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      if (child.classList && child.classList.contains("reveal-w")) {
        collector.push(child);
      } else {
        wrapWords(child, collector);
      }
    }
  }
}
